import React, { memo } from "react";
import { useFormik } from "formik";
import { Checkbox, FormControlLabel } from "@mui/material";
import { DynamicFormProps } from "@/models/dynamic-form.model";
import * as Yup from "yup";
import { InputFieldType } from "@/constants/enums/input-field-type.enum";

const SimpleDynamicForm = memo<DynamicFormProps>(
  ({
    fields,
    initialValues = {},
    onSubmit,
    submitButtonText = "Save",
    readOnly = false,
  }) => {
    const validationSchema = Yup.object().shape(
      fields.reduce(
        (acc, field) => ({
          ...acc,
          [field.name]: field.validation || Yup.string(),
        }),
        {},
      ),
    );

    const formik = useFormik({
      initialValues,
      validationSchema,
      onSubmit: async (values) => {
        await onSubmit(values);
      },
    });

    return (
      <div>
        {fields.map((field, index) => (
          <div key={field.name}>
            {field.type === InputFieldType.CHECKBOX && (
              <div className="tw-mb-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(formik.values[field.name])}
                      onChange={(e) => {
                        formik.setFieldValue(field.name, e.target.checked);
                      }}
                      name={field.name}
                      disabled={readOnly}
                      sx={{
                        color: "#E9EAEB",
                        "&.Mui-checked": { color: "#146C94" },
                        ".PrivateSwitchBase-input": {
                          display: "none",
                        },
                        "&.MuiCheckbox-root": {
                          paddingTop: "0px !important",
                          alignSelf: "start !important",
                        },
                      }}
                    />
                  }
                  label={field.label}
                />
              </div>
            )}

            {field.type === InputFieldType.TEXTAREA && (
              <div className="tw-mb-4">
                <label className="tw-block tw-text-[#232323] tw-text-sm tw-mb-1">
                  {field.label}
                </label>
                <textarea
                  name={field.name}
                  value={formik.values[field.name] || ""}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={readOnly}
                  placeholder="Enter a description..."
                  className="tw-w-full tw-p-2 tw-border tw-rounded-md tw-text-sm"
                  rows={3}
                />
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <div className="tw-text-red-500 tw-text-xs">
                    {formik.errors[field.name] as string}
                  </div>
                )}
              </div>
            )}
            {field.type === InputFieldType.BULLET_LIST && (
              <div className="tw-text-[#232323] tw-text-sm">
                <ul className="tw-list-disc tw-pl-5">
                  <li className="tw-text-[#232323] tw-text-sm tw-mb-1">
                    {field.label}
                  </li>
                </ul>
              </div>
            )}
            {field.type === InputFieldType.NORMAL_TEXT && (
              <div className="tw-text-gray-500 tw-ml-4 tw-text-sm tw-mb-4">
                {field.label}
              </div>
            )}
          </div>
        ))}

        {fields.some(
          (field) =>
            field.type === InputFieldType.CHECKBOX ||
            field.type === InputFieldType.TEXTAREA,
        ) &&
          !readOnly && (
            <button
              type="button"
              onClick={() => formik.handleSubmit()}
              disabled={!formik.isValid || formik.isSubmitting}
              className="tw-w-full tw-bg-[#146C94] tw-text-white tw-px-4 tw-py-2 tw-rounded-lg hover:tw-bg-[#105C84]"
            >
              {formik.isSubmitting ? "Saving..." : submitButtonText}
            </button>
          )}
      </div>
    );
  },
);

SimpleDynamicForm.displayName = "SimpleDynamicForm";

export default SimpleDynamicForm;
